// Bates lookup for clickable cites in a chat answer.
//
// GET /cases/{id}/bates-lookup?bates=... resolves a Bates number to a document
// (exact bates_begin, else range containment) or answers 404. The cache is keyed
// by case and Bates number:
//   - a hit is kept for the whole session;
//   - a miss (404) is kept for 60 s, so a false positive is not asked about twice
//     in a row but a document loaded a minute later can still be found;
//   - a request that is already in flight is shared by every caller;
//   - a transport error is NEVER cached: the next click asks again.
// Cleared on sign-out together with the other per-user caches (AuthContext).
import { ApiResponseError, api } from '../api';

export interface BatesDocument {
  match: 'document';
  document_id: string;
  bates_begin: string;
  bates_end: string | null;
  original_filename: string | null;
  page_count: number | null;
  doc_type: string | null;
}

export type BatesLookupResult = { status: 'hit'; doc: BatesDocument } | { status: 'miss' };

export const BATES_MISS_TTL_MS = 60_000;

type Settled = { result: BatesLookupResult; at: number };
const settled = new Map<string, Settled>();
const inFlight = new Map<string, Promise<BatesLookupResult>>();

const keyOf = (caseId: string, bates: string) => `${caseId}|${bates.trim().toUpperCase()}`;

/** The cached answer, if any, without asking the server. */
export function peekBates(caseId: string, bates: string, now: number = Date.now()): BatesLookupResult | undefined {
  const key = keyOf(caseId, bates);
  const hit = settled.get(key);
  if (!hit) return undefined;
  if (hit.result.status === 'miss' && now - hit.at >= BATES_MISS_TTL_MS) {
    settled.delete(key);
    return undefined;
  }
  return hit.result;
}

export function lookupBates(caseId: string, bates: string, now: number = Date.now()): Promise<BatesLookupResult> {
  const cached = peekBates(caseId, bates, now);
  if (cached) return Promise.resolve(cached);
  const key = keyOf(caseId, bates);
  const pending = inFlight.get(key);
  if (pending) return pending;

  const request = api
    .get<BatesDocument>(`/cases/${encodeURIComponent(caseId)}/bates-lookup?bates=${encodeURIComponent(bates.trim())}`)
    .then(
      (doc): BatesLookupResult => {
        const result: BatesLookupResult = { status: 'hit', doc };
        settled.set(key, { result, at: now });
        return result;
      },
      (err: unknown): BatesLookupResult => {
        if (err instanceof ApiResponseError && err.status === 404) {
          const result: BatesLookupResult = { status: 'miss' };
          settled.set(key, { result, at: now });
          return result;
        }
        throw err; // not cached: the next click asks again
      },
    )
    .finally(() => {
      inFlight.delete(key);
    });
  inFlight.set(key, request);
  return request;
}

export function clearBatesLookupCache(): void {
  settled.clear();
  inFlight.clear();
}
